import { Box, Flex, HStack, Text, chakra, useRecipe } from '@chakra-ui/react';
import React from 'react';

import type { Route } from 'nextjs-routes';
import { route } from 'nextjs-routes';

import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { Tag } from 'toolkit/chakra/tag';
import { Tooltip } from 'toolkit/chakra/tooltip';
import { Hint } from 'toolkit/components/Hint/Hint';
import { recipe } from 'toolkit/theme/recipes/card.recipe';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import TruncatedValue from 'ui/shared/TruncatedValue';

export type Props = {
  className?: string;
  label: string;
  value: string | React.ReactNode;
  valuePrefix?: string;
  valuePostfix?: string | React.ReactNode;
  hint?: string | React.ReactNode;
  endComponent?: React.ReactNode;
  isLoading?: boolean;
  diff?: string | number;
  diffFormatted?: string;
  diffPeriod?: '24h';
  showDiffPeriod?: boolean;
  period?: '1h' | '24h' | '30min';
  href?: Route;
  icon?: IconName | React.ReactNode;
  isLabelHint?: boolean;
};

const Container = ({ href, children, className }: { href?: Route; children: React.JSX.Element; className?: string }) => {
  if (href) {
    return (
      <Link href={ route(href) } variant="plain" className={ className }>
        { children }
      </Link>
    );
  }

  return children;
};

const StatsWidget = ({
  className,
  icon,
  label,
  value,
  valuePrefix,
  valuePostfix,
  isLoading,
  hint,
  isLabelHint,
  endComponent,
  diff,
  diffPeriod = '24h',
  showDiffPeriod: showDiffPerios = true,
  diffFormatted,
  period,
  href,
}: Props) => {
  const cardRecipe = useRecipe({ recipe });

  return (
    <Container href={ !isLoading ? href : undefined } className={ href ? className : undefined }>
      <Flex
        className={ href ? undefined : className }
        w="100%"
        h="100%"
        css={ cardRecipe() }
        p={ 4 }
        flexDir={{ lg: 'row', base: 'column' }}
        rowGap={ 4 }
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          columnGap={ 3 }
          w="100%"
          h="100%"
        >
          { icon && (
            <Box
              p={ 1 }
              borderRadius="full"
              { ...(!isLoading ? {
                borderWidth: '1px',
                borderColor: 'defaultBlack.100',
                bg: 'green.50',
                color: 'defaultBlack.700',
              } : {})
              }
            >
              { typeof icon === 'string' ? (
                <IconSvg
                  name={ icon as IconName }
                  p={ 2 }
                  boxSize="40px"
                  isLoading={ isLoading }
                  borderRadius="base"
                  flexShrink={ 0 }
                />
              ) : (
                icon
              ) }
            </Box>
          ) }
          <Box w={{ base: '100%', lg: icon ? 'calc(100% - 48px)' : '100%' }}>
            <Skeleton
              loading={ isLoading }
              color="text.secondary"
              textStyle="text.xs"
              w="100%"
              mb={ 1 }
              fontWeight={ 500 }
            >
              <HStack justify="space-between">
                { !isLabelHint ? (
                  <h2>{ label.toUpperCase() }</h2>
                ) : (
                  <Tooltip
                    content={ hint }
                    positioning={{ placement: 'top' }}
                  >
                    <chakra.h2 borderBottomWidth="2px" borderStyle="dotted" borderColor="green.800">{ label.toUpperCase() }</chakra.h2>
                  </Tooltip>
                ) }
                { period && <Text color="text.secondary" textStyle="xs" fontWeight={ 400 } ml={ 1 }>{ period }</Text> }
              </HStack>
            </Skeleton>
            <Skeleton
              loading={ isLoading }
              display="flex"
              alignItems="center"
              textStyle="text.xl"
              fontWeight={ 500 }
            >
              { valuePrefix && <chakra.span whiteSpace="pre">{ valuePrefix }</chakra.span> }
              { typeof value === 'string' ? (
                <TruncatedValue isLoading={ isLoading } value={ value }/>
              ) : (
                value
              ) }
              { typeof valuePostfix === 'string' ? (
                <chakra.span whiteSpace="pre">{ valuePostfix }</chakra.span>
              ) : (
                valuePostfix
              ) }
              { diff && Number(diff) > 0 && (
                <>
                  <Tag selected ml={ 2 } mr={ 1 } variant="select" size="sm" borderRadius="base">
                    +{ diffFormatted || Number(diff).toLocaleString() }
                  </Tag>
                  { showDiffPerios && <Text color="text.secondary" textStyle="sm">({ diffPeriod })</Text> }
                </>
              ) }
            </Skeleton>
          </Box>
          <Box hideBelow="sm">
            { endComponent }
          </Box>

          { !isLabelHint && (
            typeof hint === 'string' ? (
              <Skeleton loading={ isLoading } alignSelf="center" borderRadius="base">
                <Hint label={ hint } boxSize={ 5 } color={{ _light: 'text.pale', _dark: 'gray.400' }}/>
              </Skeleton>
            ) : hint
          ) }
        </Flex>
        <Box hideFrom="sm" _empty={{ display: 'none' }}>
          { endComponent }
        </Box>
      </Flex>
    </Container>
  );
};

export default chakra(StatsWidget);
